import {
	usePasswordResetValidateMutation,
	useResendPasswordResetRequestMutation,
} from '@codezo/graphql';
import { useThemeColors } from '@codezo/theme';
import {
	Alert,
	Anchor,
	Box,
	Button,
	Group,
	Paper,
	PinInput,
	Stack,
	Text,
	Title,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { IconAlertCircle, IconArrowLeft, IconCheck } from '@tabler/icons-react';
import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Logo } from '../components/common/Logo';

export function ResetPasswordValidatePage() {
	const [code, setCode] = useState('');
	const [error, setError] = useState<string | null>(null);
	const [resendSuccess, setResendSuccess] = useState(false);
	const navigate = useNavigate();
	const location = useLocation();

	const colors = useThemeColors();
	const isMobile = useMediaQuery('(max-width: 48em)');

	// Get token and email from navigation state
	const token = location.state?.token as string | undefined;
	const email = location.state?.email as string | undefined;

	useEffect(() => {
		if (!token || !email) {
			navigate('/auth/forgot-password');
		}
	}, [token, email, navigate]);

	const [validateReset, { loading }] = usePasswordResetValidateMutation({
		onCompleted: (data) => {
			if (data.passwordResetValidate.success) {
				// Navigate to new password page with state
				navigate('/auth/reset-password/new', {
					state: {
						token,
						validated: true,
					},
				});
			}
		},
		onError: (err) => {
			setError(err.message || 'Invalid or expired verification code. Please try again.');
		},
	});

	const [resendCode, { loading: resendLoading }] = useResendPasswordResetRequestMutation({
		onCompleted: (data) => {
			if (data.resendPasswordResetRequest) {
				setResendSuccess(true);
				// Update navigation state with new token
				navigate('/auth/reset-password/validate', {
					state: {
						token: data.resendPasswordResetRequest,
						email,
					},
					replace: true,
				});
				setTimeout(() => setResendSuccess(false), 5000);
			}
		},
		onError: (err) => {
			setError(err.message || 'Failed to resend code. Please try again.');
		},
	});

	const handleValidate = async () => {
		if (!token || code.length !== 6) return;

		setError(null);

		try {
			await validateReset({
				variables: {
					input: {
						token,
						code,
					},
				},
			});
		} catch {
			// Error is already handled by onError callback
			// Error handled by mutation onError
		}
	};

	const handleResend = async () => {
		if (!email) {
			navigate('/auth/forgot-password');
			return;
		}

		try {
			await resendCode({
				variables: {
					input: {
						authKey: email,
					},
				},
			});
		} catch {
			// Error handled by mutation onError
		}
	};

	return (
		<Box style={{ position: 'relative' }}>
			<motion.div
				initial={{ opacity: 0, y: 20 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.5 }}
			>
				<Paper
					p={{ base: 'xs', sm: 'xl' }}
					radius="md"
					miw={{ base: 'calc(100vw - 20px)', xs: 480 }}
				>
					<Stack>
						<Stack gap="sm" ta="center">
							<Logo height={40} />
							<div>
								<Title mb={4}>Enter Verification Code</Title>
								<Text size="sm" c={colors.textMuted}>
									We&apos;ve sent a 6-digit code to {email || 'your email'}
								</Text>
							</div>
						</Stack>

						{error && (
							<Alert
								icon={<IconAlertCircle size={20} color={colors.error} />}
								color="error"
							>
								{error}
							</Alert>
						)}

						{resendSuccess && (
							<Alert icon={<IconCheck size={20} />} color="green">
								A new verification code has been sent to your email
							</Alert>
						)}

						<Stack gap="xl" align="center">
							<PinInput
								length={6}
								type="number"
								size={isMobile ? 'md' : 'xl'}
								value={code}
								onChange={setCode}
								disabled={loading}
								onComplete={handleValidate}
								error={!!error}
							/>

							<Text size="sm" c={colors.textMuted} ta="center">
								The code will expire in 15 minutes
							</Text>
						</Stack>

						<Button
							fullWidth
							size="sm"
							onClick={handleValidate}
							loading={loading}
							disabled={code.length !== 6}
						>
							Verify Code
						</Button>

						<Group justify="center">
							<Text size="sm" c={colors.textMuted}>
								Didn&apos;t receive the code?{' '}
								<Anchor component="span" onClick={handleResend}>
									{resendLoading ? 'Sending...' : 'Resend'}
								</Anchor>
							</Text>
						</Group>

						<Group justify="center">
							<Anchor
								component={Link}
								to="/auth/forgot-password"
								style={{ display: 'flex', alignItems: 'center', gap: 4 }}
							>
								<IconArrowLeft size={16} />
								Start over
							</Anchor>
						</Group>
					</Stack>
				</Paper>
			</motion.div>
		</Box>
	);
}
